import React from 'react';
import { GlobeIcon, MailIcon, MegaphoneIcon, MessageSquareTextIcon, PresentationIcon, QrCodeIcon, Share2Icon, ThumbsUpIcon, UsersIcon } from 'lucide-react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { clusters } from '../../../data/teaser';
import { shareChannels, snakkerSupport } from '../../../data/deck';
import type { ChannelId } from '../../../data/deck';
const GLYPHS: Record<ChannelId, React.ComponentType<{
  className?: string;
}>> = {
  embed: GlobeIcon,
  messaging: MessageSquareTextIcon,
  detailer: PresentationIcon,
  qr: QrCodeIcon,
  post: ThumbsUpIcon,
  email: MailIcon,
  advertise: MegaphoneIcon,
  congress: UsersIcon
};
export function ChannelsSlide() {
  return <Slide id="channels" data-id="element-113" data-mp="3gr5lao90c2kl">
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-114" data-mp="3gqclk78gudby">
        <InView data-id="element-115" data-mp="3bt3z3ugabn4w">
          <SlideHeadline lead="One experience." follow="More channels to share." data-id="element-116" data-mp="3brj7odp9okb9" />
        </InView>
        <InView delay={0.06} data-id="element-117" data-mp="1wqz528tn4opx">
          <p className="text-[14.5px] leading-[1.7] text-navy/65" data-id="element-118" data-mp="1wtv11rlxh3bq">
            A single Snak can meet retina specialists wherever attention already exists — without rebuilding the
            experience for every channel.
          </p>
        </InView>
      </div>

      <div className="mt-14 grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-10" data-id="element-119" data-mp="2bnq60miyq8hn">
        <InView delay={0.1} y={26} data-id="element-120" data-mp="1e2lr5h88wcjm">
          <figure className="flex h-full flex-col rounded-[30px] bg-navy p-6 sm:p-7" data-id="element-121" data-mp="1dz6dmiu7zbkb">
            <div className="flex items-center gap-4" data-id="element-122" data-mp="1j0n9fzxuk7u1">
              <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-paper/[0.08] text-mint" data-id="element-123" data-mp="1nz6w62k9lnng">
                <GlobeIcon className="h-5 w-5" aria-hidden="true" data-id="element-124" data-mp="1sqyr0pagefje" />
              </span>
              <div data-id="element-125" data-mp="v9q8tjbi4wy3">
                <span className="block text-[10px] uppercase tracking-[0.22em] text-mint" data-id="element-126" data-mp="va9r5152dncn">Example entry point</span>
                <span className="mt-1 block font-display text-[24px] font-semibold leading-none text-paper" data-id="element-127" data-mp="107rqq0x6yq7z">
                  The DME phenotyping Snak
                </span>
              </div>
            </div>
            <div className="mt-6 grid flex-1 grid-cols-2 gap-2 rounded-[22px] bg-paper/[0.06] p-3" data-id="element-128" data-mp="n894qw6y7vrr">
              {clusters.map((cluster, i) => <div key={cluster.id} className="overflow-hidden rounded-[14px] bg-[#0A0F14]" data-id="element-129" data-mp="x0y1sm1r573k" data-mpi={i}>
                  <img src={cluster.image} alt={`${cluster.name} — ${cluster.descriptor}`} className="block aspect-[4/3] h-auto w-full object-cover object-top" data-id="element-130" data-mp="99izgt9z6psb" />
                  <p className="px-3 py-2 text-[11px] text-paper/70" data-id="element-131" data-mp="j6f76ws1v7vt">CL {i + 1} · {cluster.descriptor}</p>
                </div>)}
            </div>
          </figure>
        </InView>

        <div className="flex flex-col" data-id="element-132" data-mp="1ulu5c3vzw5si">
          <InView delay={0.14} data-id="element-133" data-mp="1pou5wbtni1ad">
            <div className="flex items-end justify-between gap-6" data-id="element-134" data-mp="1pnsjxcb3judv">
              <div data-id="element-135" data-mp="1x7k02w3w5pp3">
                <span className="text-[10px] uppercase tracking-[0.22em] text-navy/45" data-id="element-136" data-mp="1x71u37rffx3z">The same Snak</span>
                <h3 className="mt-2 font-display text-[28px] font-semibold leading-[1.08] tracking-[-0.02em] text-navy sm:text-[34px]" data-id="element-137" data-mp="227zbhi4m4n4f">
                  Ready for every relevant touchpoint
                </h3>
              </div>
              <span className="hidden shrink-0 rounded-full bg-navy px-3.5 py-1.5 text-[10px] uppercase tracking-[0.16em] text-mint sm:inline-flex" data-id="element-138" data-mp="1sb30n5r8rowp">
                One build · many entries
              </span>
            </div>
          </InView>

          <ul className="mt-7 grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-139" data-mp="2772hp5wbwzdz">
            {shareChannels.map((channel, i) => {
            const Icon = GLYPHS[channel.id];
            return <li key={channel.id} data-id="element-140" data-mp="2yx6xssd2309e" data-mpi={i}>
                  <InView delay={0.18 + i * 0.03} className="flex h-full min-h-[170px] flex-col justify-between rounded-[20px] bg-paper p-5 ring-1 ring-navy/[0.08]" data-id="element-141" data-mp="2yw5lylo7u9qm">
                    <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-canvas text-navy" data-id="element-142" data-mp="1fxhh30zv57ew">
                      <Icon className="h-5 w-5" aria-hidden="true" data-id="element-143" data-mp="161ebfl5syqn7" />
                    </span>
                    <span className="mt-6" data-id="element-144" data-mp="120camzw8m6j4">
                      <span className="block font-display text-[17px] font-semibold leading-tight text-navy" data-id="element-145" data-mp="121dwlzeskdfm">
                        {channel.name}
                      </span>
                      <span className="mt-1 block text-[12px] leading-snug text-navy/55" data-id="element-146" data-mp="1tqz0mlsjuk4x">{channel.detail}</span>
                    </span>
                  </InView>
                </li>;
          })}
          </ul>

          <InView delay={0.4} data-id="element-147" data-mp="1kyf4pt3n6tns">
            <p className="mt-3 flex items-center gap-4 rounded-[20px] bg-paper px-5 py-5 font-display text-[18px] font-medium leading-snug text-navy ring-1 ring-navy/[0.08] sm:text-[20px]" data-id="element-148" data-mp="1kx405r2dhhvm">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-mint text-navy" data-id="element-149" data-mp="2co9vlu75erek">
                <Share2Icon className="h-4 w-4" aria-hidden="true" data-id="element-150" data-mp="46jn5b32wpf4" />
              </span>
              Consistent experience, consistent measurement — regardless of where the journey begins.
            </p>
          </InView>
        </div>
      </div>

      <InView delay={0.2} data-id="element-151" data-mp="d81754jlxb93">
        <div className="mt-14 grid gap-8 border-t border-navy/[0.12] pt-10 lg:grid-cols-2 lg:items-end lg:gap-16" data-id="element-152" data-mp="d558fdzxqijl">
          <p className="max-w-[26ch] font-display text-[28px] font-semibold leading-[1.08] tracking-[-0.02em] text-navy sm:text-[36px]" data-id="element-153" data-mp="n5fi4omrh7u0">
            Creative consistency at regional level.{' '}
            <span className="text-navy/45" data-id="element-154" data-mp="vxpv2dp1jrey">Relevant activation at local level.</span>
          </p>
          <div data-id="element-155" data-mp="10wu3h2455l48">
            <span className="text-[10px] uppercase tracking-[0.2em] text-navy/45" data-id="element-156" data-mp="10x2e0ncskec9">
              Snakker supports local brand teams with
            </span>
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-157" data-mp="15w6nzg7jig3n">
              {snakkerSupport.map(item => <span key={item} className="rounded-full border border-navy/20 px-3 py-1 text-[12px] text-navy" data-id="element-158" data-mp="31wyzspcd6y3s">
                  {item}
                </span>)}
            </div>
            <p className="mt-3 text-[12px] text-navy/55" data-id="element-159" data-mp="3bq6384njo418">Practical support — not a new platform or an extra layer.</p>
          </div>
        </div>
      </InView>
    </Slide>;
}